import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Page_Archive.module.css";
import { useSelector } from "react-redux";

const ALL_STATUSES = ["Closed", "Withdrawn", "Expired"];
const RA_SUB_TYPES = ["Open", "Closed"];
const RFP_SUB_TYPES = ["Intrastate", "Interstate"];

const PRODUCT_TYPE_MAP = [
  { name: "Bilateral", always: true },
  { name: "RFP/RFQ", productName: "RFP_RFQ" },
  { name: "Reverse Auction", productName: "RA" },
  { name: "TradeConnect", productName: "TRADE_CONNECT" },
];

const ArchiveHeader = ({
  onBack,
  initialType = "Bilateral",
  initialStatus = "Closed",
  initialRaSubType = "Open",
  initialRfpSubType = "Intrastate",
  onTypeChange,
  onStatusChange,
  onRaSubTypeChange,
  onRfpSubTypeChange,
  types = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  )?.toLowerCase();
  const mapingProduct = useSelector(
    (state) => state?.platformSlice?.userPaymentConfigs,
  );

  const accessibleTypes = useMemo(() => {
    return PRODUCT_TYPE_MAP.filter(
      (t) =>
        t.always ||
        mapingProduct?.find((p) => p?.productName === t.productName),
    ).map((t) => t.name);
  }, [mapingProduct]);

  const effectiveTypes = types ?? accessibleTypes;
  console.debug(
    "effectiveTypes",
    effectiveTypes,
    PRODUCT_TYPE_MAP,
    mapingProduct,
  );

  const [type, setType] = useState(params.get("type") || initialType);
  const [status, setStatus] = useState(params.get("status") || initialStatus);
  const [raSubType, setRaSubType] = useState(
    params.get("raSubType") || initialRaSubType,
  );
  const [rfpSubType, setRfpSubType] = useState(
    params.get("rfpSubType") || initialRfpSubType,
  );

  const STATUSES = (() => {
    if (role === "generator" && type !== "Bilateral") {
      return ["Closed", "Expired"];
    }
    return ALL_STATUSES;
  })();

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    search.set("type", type);
    search.set("status", status);
    if (type === "RA") {
      search.set("raSubType", raSubType);
    } else {
      search.delete("raSubType");
    }
    if (type === "RFP") {
      search.set("rfpSubType", rfpSubType);
    } else {
      search.delete("rfpSubType");
    }
    navigate(
      { pathname: location.pathname, search: `?${search.toString()}` },
      { replace: true },
    );

    if (typeof onTypeChange === "function") onTypeChange(type);
    if (typeof onStatusChange === "function") onStatusChange(status);
    if (typeof onRaSubTypeChange === "function") onRaSubTypeChange(raSubType);
    if (typeof onRfpSubTypeChange === "function")
      onRfpSubTypeChange(rfpSubType);
  }, [
    type,
    status,
    raSubType,
    rfpSubType,
    location.search,
    location.pathname,
    navigate,
    onTypeChange,
    onStatusChange,
    onRaSubTypeChange,
    onRfpSubTypeChange,
  ]);

  useEffect(() => {
    if (
      role === "generator" &&
      status === "Withdrawn" &&
      type !== "Bilateral"
    ) {
      setStatus("Closed");
    }
  }, [type, status]);

  const handleBack = () => {
    if (typeof onBack === "function") return onBack();
    navigate(-1);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.left}>
          <button
            aria-label="Back"
            onClick={handleBack}
            className={styles.backButton}
          >
            ← Back
          </button>

          <div className={styles.tabs}>
            {effectiveTypes.map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                aria-pressed={t === type}
                className={`${styles.tab} ${t === type ? styles.tabActive : ""}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.tabs}>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              aria-pressed={s === status}
              className={`${styles.tab} ${s === status ? styles.tabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {type === "RA" && (
        <div className={styles.raSubTypeBar}>
          <span className={styles.raSubTypeLabel}>RA Type:</span>
          {RA_SUB_TYPES.map((s) => (
            <button
              key={s}
              onClick={() => setRaSubType(s)}
              aria-pressed={s === raSubType}
              className={`${styles.raSubTypeTab} ${s === raSubType ? styles.raSubTypeTabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {type === "RFP" && (
        <div className={styles.raSubTypeBar}>
          <span className={styles.raSubTypeLabel}>RFP Type:</span>
          {RFP_SUB_TYPES.map((s) => (
            <button
              key={s}
              onClick={() => setRfpSubType(s)}
              aria-pressed={s === rfpSubType}
              className={`${styles.raSubTypeTab} ${s === rfpSubType ? styles.raSubTypeTabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default ArchiveHeader;
